// How the AI picks a spell target. One heuristic per preference, less what
// the stop after a cast costs a piece of the caster. A spell with an
// `aiScore` weighs that cost itself. A later pass can give each AI
// personality its own table.

import { pieceValue } from '../engine/evaluate';
import { ranksAdvanced } from '../engine/squares';
import type { Piece, Side } from '../engine/types';
import type { GameState } from '../game/state';
import type { Rng } from '../lib/rng';
import { castStopCost, ROYAL_PICK_BONUS } from '../spells/helpers';
import type { AiPreference, Spell } from '../spells/types';

type Scorer = (piece: Piece, square: number, caster: Side) => number;

const SCORERS: Record<Exclude<AiPreference, 'random'>, Scorer> = {
  valuable: (piece) => pieceValue(piece),
  cheap: (piece) => -pieceValue(piece),
  royal: (piece) => (piece.royal ? ROYAL_PICK_BONUS : 0) + pieceValue(piece),
  advanced: (piece, square) =>
    piece.owner === 'neutral' ? 0 : ranksAdvanced(square, piece.owner) * 100 + pieceValue(piece),
};

const preferenceForPick = (spell: Spell, pickIndex: number): AiPreference => {
  if (typeof spell.aiPreference === 'string') return spell.aiPreference;
  return spell.aiPreference[pickIndex] ?? spell.aiPreference[0] ?? 'valuable';
};

type ScoredTarget = { square: number; score: number };

/** The score of each candidate for the next pick, in the order of the candidates. */
const scoreTargets = (
  state: GameState,
  candidates: readonly number[],
  spell: Spell,
  caster: Side,
  rng: Rng,
): ScoredTarget[] => {
  const picked = state.pendingTargets;
  const preference = preferenceForPick(spell, picked.length);
  const scorer = preference === 'random' ? null : SCORERS[preference];
  return candidates.map((square) => {
    const piece = state.board[square];
    if (spell.aiScore) return { square, score: spell.aiScore(state, square, caster, picked) };
    // An empty square is a destination, as in the second pick of a move spell.
    // A spell with no score of its own changes the piece it picks, and a
    // piece of the caster that a spell changed cannot move this turn.
    if (piece && scorer) {
      return { square, score: scorer(piece, square, caster) - castStopCost(state, square, caster) };
    }
    return { square, score: rng.next() };
  });
};

export const chooseSpellTarget = (
  state: GameState,
  candidates: readonly number[],
  spell: Spell,
  caster: Side,
  rng: Rng,
): number | null => {
  if (candidates.length === 0) return null;
  const preference = preferenceForPick(spell, state.pendingTargets.length);
  if (preference === 'random' && !spell.aiScore) return rng.pick(candidates);
  let best: ScoredTarget | null = null;
  for (const target of scoreTargets(state, candidates, spell, caster, rng)) {
    if (!best || target.score > best.score) best = target;
  }
  return best?.square ?? null;
};

/** The candidates for the next pick, the one `chooseSpellTarget` likes most first. */
export const rankSpellTargets = (
  state: GameState,
  candidates: readonly number[],
  spell: Spell,
  caster: Side,
  rng: Rng,
): number[] =>
  scoreTargets(state, candidates, spell, caster, rng)
    .map((target, index) => ({ ...target, index }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((target) => target.square);
